import React, { useEffect, lazy } from 'react'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import { ErrorBoundary } from './components/error-boundary'
import { fillStore } from './store'
import features from './constants/features-per-country'
import { SITE_ID } from './constants'
import { PrivateRoute } from './components/private-route'
import MarketingUTM from './components/MarketingUTM'
import ModuleNotFound from './components/ModuleNotFound'
import path from './routesCustom'

const PanelLangProvider = lazy(() => import('@panel/i18n/LangProvider').catch(() => ({ default: ModuleNotFound })))
const PanelConfigContextProvider = lazy(() =>
  import('@panel/config/context')
    .then(({ ConfigContextProvider }) => ({ default: ConfigContextProvider }))
    .catch(() => ({ default: ModuleNotFound }))
)

const Selecta = lazy(() => import('@selecta/pages/selecta').catch(() => ({ default: ModuleNotFound })))
const Panel = lazy(() => import('@panel/pages/panel').catch(() => ({ default: ModuleNotFound })))
const Bumi = lazy(() => import('./pages/bumi'))
const Config = lazy(() => import('./pages/config'))
const ResumenCuenta = lazy(() => import('./pages/resumen-cuenta'))
const ErrorPage = lazy(() => import('./components/ErrorPage'))
const TableroSeleccion = lazy(() => import('./pages/tablero-seleccion'))
const SignIn = lazy(() => import('./pages/sign-in'))
const SignUp = lazy(() => import('./pages/sign-up'))
const TalentPool = lazy(() => import('./pages/talent-pool'))
const Curriculum = lazy(() => import('./pages/curriculum'))
const Ecommerce = lazy(() => import('./pages/ecommerce'))
const ChangePassword = lazy(() => import('./pages/change-password'))
const ForgotPassword = lazy(() => import('./pages/forgot-password'))
const CompanyProfile = lazy(() => import('./pages/company-profile'))
const LocationWatcher = lazy(() => import('./components/LocationWatcher'))
const UserProfile = lazy(() => import('./pages/user-profile'))
const HomePage = lazy(() => import('./pages/home-page'))
const UserLog = lazy(() => import('./pages/user-log'))
const UserAdmin = lazy(() => import('./pages/user-admin'))
const FutureTalents = lazy(() => import('./pages/future-talents'))
const ScrollRestoriation = lazy(() => import('./components/ScrollRestoration'))
const Aviso = lazy(() => import('./pages/aviso'))
const Avisos = lazy(() => import('./pages/avisos'))
const Dashboard = lazy(() => import('./pages/dashboard'))
const Recomendados = lazy(() => import('./pages/recomendados'))
const RecomendadosCv = lazy(() => import('./pages/curriculum'))
const Estadisticas = lazy(() => import('./pages/estadisticas'))
const BusquedaEnBase = lazy(() => import('./pages/busqueda-en-base'))
const Politicas = lazy(() => import('./pages/politicas'))
const Mensajes = lazy(() => import('./pages/mensajes'))

const Layout = lazy(() => import('./components/layouts/Layout'))
const LayoutMenu = lazy(() => import('./components/layouts/LayoutMenu'))
const LayoutMenuDesktop = lazy(() => import('./components/layouts/LayoutMenuDesktop'))
const LayoutHome = lazy(() => import('./components/layouts/LayoutHome'))

export const RouteWithErrorBoundary = (props) => {
  return (
    <ErrorBoundary {...props}>
      <Route {...props} />
    </ErrorBoundary>
  )
}

const ErrorRoutes = () => {
  return (
    <Switch>
      <RouteWithErrorBoundary
        path={['/404', '/empresas/404']}
        status={404}
        component={(props) => (
          <Layout {...props}>
            <ErrorPage statusCode="404" {...props} />
          </Layout>
        )}
      />
      <RouteWithErrorBoundary
        path={['/500', '/empresas/500']}
        status={500}
        component={(props) => (
          <Layout {...props}>
            <ErrorPage statusCode="500" {...props} />
          </Layout>
        )}
      />
      <RouteWithErrorBoundary
        path={['/503', '/empresas/503']}
        status={503}
        component={(props) => (
          <Layout {...props}>
            <ErrorPage statusCode="503" {...props} />
          </Layout>
        )}
      />
      <Redirect from="*" to="/404" />
    </Switch>
  )
}

const Root = () => {
  const { privatePath, publicPath } = path

  const HOME_PAGE = ['/empresas', '/empresas/']

  const newEcommerceEnabled = features[SITE_ID || 'BMAR']?.newEcommerce?.enabled
  const resumenCuentaEnabled = features[SITE_ID || 'BMAR']?.resumenCuenta?.enabled

  useEffect(() => {
    fillStore()
  }, [])

  return (
    <Router>
      <ScrollRestoriation />
      <LocationWatcher />
      <MarketingUTM />
      <Switch>
        <RouteWithErrorBoundary
          path={HOME_PAGE}
          exact
          component={(props) => (
            <LayoutHome {...props}>
              <HomePage {...props} />
            </LayoutHome>
          )}
        />
        <RouteWithErrorBoundary
          exact
          path={publicPath.SIGN_IN}
          component={() => (
            <Layout>
              <SignIn />
            </Layout>
          )}
        />
        <RouteWithErrorBoundary
          exact
          path={privatePath.TABLERO_SELECCION}
          component={(props) => (
            <PrivateRoute>
              <Layout>
                <TableroSeleccion {...props} />
              </Layout>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.CURRICULUM}
          component={(props) => (
            <PrivateRoute>
              <Curriculum {...props} />
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.TALENT_POOL}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <TalentPool {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.AVISO}
          component={(props) => (
            <PrivateRoute>
              <Layout>
                <Aviso {...props} />
              </Layout>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.AVISOS}
          component={(props) => (
            <PrivateRoute>
              <Avisos {...props} />
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.DASHBOARD}
          component={(props) => (
            <PrivateRoute>
              <Dashboard {...props} />
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.FUTURE_TALENTS}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <FutureTalents {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.ADMIN_USUARIOS}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <UserAdmin {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.USUARIOS_LOG}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <UserLog {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        {newEcommerceEnabled && (
          <RouteWithErrorBoundary
            path={privatePath.NEW_ECOMMERCE}
            component={(props) => (
              <PrivateRoute>
                <Ecommerce {...props} />
              </PrivateRoute>
            )}
          />
        )}
        <RouteWithErrorBoundary
          path={privatePath.PERFIL_USUARIO}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <UserProfile {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.COMPANY_PROFILE}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <CompanyProfile {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={publicPath.SIGN_UP}
          component={(props) => (
            <Layout>
              <SignUp {...props} />
            </Layout>
          )}
        />
        <RouteWithErrorBoundary
          path={publicPath.FORGOT_PASSWORD}
          component={(props) => (
            <Layout>
              <ForgotPassword {...props} />
            </Layout>
          )}
        />
        <RouteWithErrorBoundary
          path={publicPath.CHANGE_PASSWORD}
          component={(props) => (
            <Layout>
              <ChangePassword {...props} />
            </Layout>
          )}
        />
        {resumenCuentaEnabled && (
          <RouteWithErrorBoundary
            exact
            path={privatePath.RESUMEN_CUENTA}
            component={(props) => (
              <PrivateRoute>
                <LayoutMenuDesktop>
                  <ResumenCuenta {...props} />
                </LayoutMenuDesktop>
              </PrivateRoute>
            )}
          />
        )}
        <RouteWithErrorBoundary
          path={privatePath.SELECTA}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <Selecta {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.PANEL}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenu>
                <PanelLangProvider>
                  <PanelConfigContextProvider>
                    <Panel {...props} />
                  </PanelConfigContextProvider>
                </PanelLangProvider>
              </LayoutMenu>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.ESTADISTICAS}
          component={(props) => (
            <PrivateRoute>
              <Layout>
                <Estadisticas {...props} />
              </Layout>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary path="/empresas/config" component={() => <Config />} />
        <RouteWithErrorBoundary
          path={privatePath.JOBI}
          component={() => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <Bumi />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.RECOMENDADOS}
          component={(props) => (
            <PrivateRoute>
              <Layout>
                <Recomendados {...props} />
              </Layout>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.CV_RECOMENDADO}
          component={(props) => (
            <PrivateRoute>
              <RecomendadosCv {...props} />
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.BUSQUEDA_EN_BASE}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <BusquedaEnBase {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <RouteWithErrorBoundary
          path={publicPath.POLICY}
          component={(props) => (
            <LayoutHome {...props}>
              <Politicas {...props} />
            </LayoutHome>
          )}
        />
        <RouteWithErrorBoundary
          path={privatePath.MENSAJES}
          component={(props) => (
            <PrivateRoute>
              <LayoutMenuDesktop>
                <Mensajes {...props} />
              </LayoutMenuDesktop>
            </PrivateRoute>
          )}
        />
        <ErrorRoutes />
      </Switch>
    </Router>
  )
}
export default Root
